import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { MappingService, FrontendUserRole } from '@tenant/shared'
import { apiClient } from '../services/apiClient'
import { setAuthToken, clearAuthToken } from '../services/api'
import { adminSocketService } from '../services/socketService'
import { useTenant } from './TenantContext'
import { storage } from '../lib/storage'

interface User {
  id: string
  email: string
  name: string
  role: string
  frontendRole: FrontendUserRole
  avatar?: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (identifier: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

const mapToFrontendRole = (rawUser: any): FrontendUserRole => {
  return MappingService.mapRoleToFrontend({
    role: rawUser.role,
    isFloristManager: rawUser.isFloristManager,
    isDeliveryManager: rawUser.isDeliveryManager,
  } as any)
}

const normalizeUser = (rawUser: any): User => ({
  id: rawUser.id,
  email: rawUser.email,
  name: rawUser.name,
  role: rawUser.role,
  frontendRole: mapToFrontendRole(rawUser),
  avatar: rawUser.avatar,
})

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const tenant = useTenant()

  useEffect(() => {
    // Wait for TenantProvider to resolve (window.__TENANT_SLUG__ set) before
    // attempting to read auth_token — without slug, storage returns null, and
    // we'd false-negative-logout the user on every refresh.
    if (!tenant) return

    const checkAuth = async () => {
      try {
        const response = await apiClient.auth.me()
        setUser(normalizeUser(response.user))
        const storedToken = storage.authToken.get()
        if (storedToken) {
          adminSocketService.updateToken(storedToken)
        }
      } catch (error) {
        clearAuthToken()
      } finally {
        setIsLoading(false)
      }
    }

    const token = storage.authToken.get()
    if (token) {
      checkAuth()
    } else {
      setIsLoading(false)
    }
  }, [tenant])

  const login = async (identifier: string, password: string) => {
    const isPhone = identifier.startsWith('+') || /^[\d\s\-()]+$/.test(identifier)
    const normalizedIdentifier = isPhone
      ? identifier.replace(/[\s\-()]/g, '')
      : identifier

    const payload = isPhone
      ? { phone: normalizedIdentifier, password }
      : { email: normalizedIdentifier, password }

    const response = await apiClient.auth.login(payload as any)
    setAuthToken(response.token)
    adminSocketService.updateToken(response.token)
    setUser(normalizeUser(response.user))
  }

  const logout = () => {
    adminSocketService.disconnect()
    clearAuthToken()
    setUser(null)
  }

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

